import React, { FC, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Game } from '../utils/gameUtils';
import { playPressSound, playLoseSound } from '../utils/soundUtils';
import GameMode from './GameMode';

interface GameStats {
  gamesPlayed: number;
  maxRound: number;
  customStat: number;
  gameName?: string;
}

interface CurrentGameStats {
  customStat: number;
}

interface GameLostProps {
  currentRound: number;
  onRestart: () => void;
  onExit: () => void;
  isRestartCooldown: boolean;
  cooldownSeconds: number;
  countdownKey: number;
  stats: GameStats;
  currentGameStats: CurrentGameStats;
  customStatLabel: string;
  customStatLabelSingular: string;
  gameInstructions: string[];
  gameName: string;
  game: Game;
  sessionStartBest: number;
  currentGameMode: string | null;
  onGameModeChange: (modeId: string) => void;
  isDarkMode?: boolean;
  startCooldown: () => void; // New prop to start cooldown
}

const GameLost: FC<GameLostProps> = ({
  currentRound,
  onRestart,
  onExit,
  isRestartCooldown,
  cooldownSeconds,
  countdownKey,
  stats,
  currentGameStats,
  customStatLabel,
  customStatLabelSingular,
  gameInstructions,
  gameName,
  game,
  sessionStartBest,
  currentGameMode,
  onGameModeChange,
  isDarkMode = false,
  startCooldown, // New prop
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [localCountdownKey, setLocalCountdownKey] = useState(countdownKey);
  const [previousBestValue, setPreviousBestValue] = useState(sessionStartBest);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLocalCountdownKey(countdownKey);
  }, [countdownKey]);

  useEffect(() => {
    setPreviousBestValue(sessionStartBest);
  }, [sessionStartBest]);

  const recordMessage = useMemo(() => {
    const isNewRecord = currentRound > previousBestValue;
    const isTie = currentRound === previousBestValue;
    const isFirstGame = previousBestValue === 0;

    if (isFirstGame) {
      return 'primer';
    } else if (isNewRecord) {
      return 'nou';
    } else if (isTie) {
      return 'igualat';
    } else {
      return 'millor';
    }
  }, [currentRound, previousBestValue]);

  const customStatValue =
    currentGameStats?.customStat !== undefined
      ? currentGameStats.customStat
      : 0;

  const handleRestartClick = () => {
    if (isRestartCooldown) return;
    playPressSound();
    startCooldown(); // Start the cooldown
    onRestart();
  };

  return (
    <div className="space-y-6">
      <div
        className={`text-gray-600 dark:text-gray-300 ${
          isDarkMode && currentGameMode
            ? 'bg-gradient-to-r from-yellow-500 to-red-500 p-4 rounded-lg text-white'
            : ''
        }`}
      >
        <h3 className="text-lg font-semibold mb-4 text-center">
          <div className="space-y-4">
            <div
              className="text-2xl font-bold relative"
              style={{
                color: isDarkMode && currentGameMode ? 'white' : '#ee5711',
              }}
            >
              Has Perdut!
            </div>
            <div className="mt-4">
              {gameInstructions.map((paragraph, index) => (
                <p key={index} className="mb-1 text-center text-base">
                  {paragraph}
                </p>
              ))}
            </div>
            <GameMode
              game={game}
              currentGameMode={currentGameMode}
              onGameModeChange={onGameModeChange}
              onRestart={onRestart}
            />
            <div
              className={`${
                isDarkMode && currentGameMode
                  ? 'bg-gray-700'
                  : 'bg-gray-100 dark:bg-gray-700'
              } p-4 mt-4 rounded-lg`}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 items-center">
                <div className="flex flex-col items-center">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Ronda
                  </div>
                  <div className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                    {currentRound}
                  </div>
                </div>

                {!isMobile && (
                  <div className="flex flex-col items-center justify-center">
                    {recordMessage === 'primer' && (
                      <div className="text-2xl font-bold text-yellow-500 dark:text-yellow-400 animate-pulse whitespace-nowrap">
                        Molt Bé!
                      </div>
                    )}
                    {recordMessage === 'nou' && (
                      <div className="text-2xl font-bold text-yellow-500 dark:text-yellow-400 animate-pulse whitespace-nowrap">
                        Nou Rècord!
                      </div>
                    )}
                    {recordMessage === 'igualat' && (
                      <div className="text-2xl font-bold text-yellow-500 dark:text-yellow-400 animate-pulse whitespace-nowrap">
                        Rècord Igualat!
                      </div>
                    )}
                    {recordMessage === 'millor' && (
                      <>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Millor Ronda
                        </div>
                        <div className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                          {previousBestValue}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="flex flex-col items-center">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {customStatLabelSingular}
                  </div>
                  <div className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                    {customStatValue}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </h3>
      </div>

      <div className="flex space-x-3 relative">
        <motion.button
          onClick={handleRestartClick}
          disabled={isRestartCooldown}
          className={`relative z-20 bg-yellow-500 hover:bg-yellow-600 text-white font-bold 
                      py-2 px-4 sm:py-2 sm:px-6 text-sm sm:text-base rounded-lg 
                      transition transform duration-300 flex items-center justify-center 
                      shadow-md hover:shadow-lg flex-1 ${
                        isRestartCooldown
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:scale-105'
                      }`}
          whileTap={{ scale: isRestartCooldown ? 1 : 0.9 }}
          whileHover={{ scale: isRestartCooldown ? 1 : 1.05 }}
        >
          <span className={`${isRestartCooldown ? 'opacity-50' : ''}`}>
            Reiniciar
          </span>
          <AnimatePresence>
            {isRestartCooldown && (
              <motion.span
                key="cooldown"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.2 }}
                className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs 
                           w-5 h-5 flex items-center justify-center rounded-full z-50"
              >
                {cooldownSeconds}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        <button
          onClick={() => {
            onExit();
          }}
          className="relative z-10 bg-[#ee5711] hover:bg-[#cc4709] text-white font-bold 
                     py-2 px-4 sm:py-2 sm:px-6 text-sm sm:text-base rounded-lg 
                     transition transform duration-300 flex items-center justify-center 
                     shadow-md hover:shadow-lg hover:scale-105 flex-1"
        >
          Sortir
        </button>
      </div>
    </div>
  );
};

export default GameLost;
